import { Body2 } from '@components/typography/Body2';
import { Body3 } from '@components/typography/Body3.tsx';
import { Body4 } from './typography/Body4';
import { ButtonSize, ButtonType } from '@common/types/button';
import { Icon } from '@components/Icon.tsx';
import { type IconType } from '@src/IconType.ts';
import { LoadingSpinner } from '@components/LoadingSpinner.tsx';
import { classNames } from '@utils/classNames';
import {
  Fragment,
  type ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Tooltip } from '@components/base/Tooltip.tsx';

export type ButtonProps = {
  label?: string | React.ReactElement;
  IconLeading?: IconType;
  IconTrailing?: IconType;
  buttonType: ButtonType;
  disabled?: boolean;
  loading?: boolean;
  iconClassName?: string;
  labelClassName?: string;
  testId?: string;
  size?: ButtonSize;
  tooltip?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const types: Record<
  ButtonType,
  { main: string; icon?: string; label?: string }
> = {
  [ButtonType.PRIMARY]: {
    main: 'text-white bg-blue-500 disabled:bg-mbGreenLight hover:brightness-90',
  },
  [ButtonType.SECONDARY]: {
    main: 'bg-white border border-blue-100 hover:brightness-90',
  },
  [ButtonType.SECONDARY_BLUE]: {
    main: 'bg-white text-blue-500 border border-blue-500 hover:brightness-90',
    label: '!text-blue-500',
  },
  [ButtonType.SECONDARY_RED]: {
    main: 'bg-white text-orange-600 border border-orange-600 hover:brightness-90',
  },
  [ButtonType.SECONDARY_BLACK]: {
    main: 'bg-white text-slate-900 border border-slate-900 hover:brightness-90',
  },
  [ButtonType.SECONDARY_NO_OUTLINE]: {
    main: 'bg-white hover:brightness-90',
  },
  [ButtonType.BLACK]: {
    main: 'text-white bg-slate-900 hover:brightness-90',
  },
  [ButtonType.ICON_ONLY]: {
    main: 'bg-transparent rounded hover:brightness-90',
  },
  [ButtonType.DANGER]: {
    main: 'border border-red-600  text-white bg-red-500 hover:brightness-90',
  },
  [ButtonType.LINK]: {
    main: 'bg-transparent text-blue-500 hover:border hover:border-blue-100 hover:bg-blue-200 border border-transparent',
    label: '!text-blue-500',
  },
};

const sizes: Record<ButtonSize, string> = {
  [ButtonSize.SMALL]: 'px-2 rounded',
  [ButtonSize.MEDIUM]: 'p-1.5 rounded-lg',
  [ButtonSize.DEFAULT]: 'p-3 rounded-xl',
};

export function Button({
  label,
  IconLeading,
  IconTrailing,
  buttonType,
  disabled = false,
  loading = false,
  iconClassName = '',
  testId,
  type = 'button',
  labelClassName,
  size = ButtonSize.DEFAULT,
  tooltip,
  ...rest
}: ButtonProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const LabelComponent = useMemo(() => {
    if (size === ButtonSize.MEDIUM) {
      return Body4;
    }
    return Body3;
  }, [size]);

  const WrapperComp = useCallback(
    ({ children }: { children: ReactElement }) => {
      if (tooltip) {
        return (
          <Tooltip
            className="bg-slate-700 rounded-lg text-center"
            content={
              <Body2 className="text-white text-sm font-normal">
                {tooltip}
              </Body2>
            }
            placement="top"
            handler={
              tooltip ? (open: boolean) => setTooltipOpen(open) : undefined
            }
            open={tooltip ? tooltipOpen : false}
          >
            {children}
          </Tooltip>
        );
      }
      return <Fragment>{children}</Fragment>;
    },
    [tooltip, tooltipOpen],
  );

  const iconSize = useMemo(() => {
    if (buttonType === ButtonType.ICON_ONLY) return 'medium';
    if (size === ButtonSize.SMALL) return 'x-small';
    if (size === ButtonSize.MEDIUM) return 'small';
    return 'medium';
  }, [size, buttonType]);

  return (
    <WrapperComp>
      <button
        {...rest}
        type={type}
        className={classNames(
          'inline-flex relative items-center font-semibold cursor-pointer',
          'disabled:opacity-60 gap-2',
          !loading && 'enabled:active:translate-y-0.5',
          'disabled:cursor-not-allowed disabled:!drop-shadow-none',
          loading && 'cursor-default',
          sizes[size],
          types[buttonType].main,
          rest.className,
        )}
        disabled={!!disabled}
        onClick={disabled || loading ? undefined : rest.onClick}
        {...(testId ? { 'data-testid': testId } : {})}
      >
        {loading && (
          <LoadingSpinner className="!h-5 absolute inset-x-0 inset-y-auto" />
        )}
        {
          <div
            className={classNames(
              'w-full inline-flex relative items-center justify-center gap-x-2',
              loading && 'invisible',
            )}
          >
            {IconLeading && (
              <Icon
                type={IconLeading}
                className={classNames(
                  types[buttonType].icon ?? '',
                  iconClassName,
                )}
                size={iconSize}
              />
            )}
            {label && (
              <LabelComponent
                className={classNames(labelClassName, types[buttonType].label)}
              >
                {label}
              </LabelComponent>
            )}
            {IconTrailing && (
              <Icon
                type={IconTrailing}
                className={classNames(
                  types[buttonType].icon ?? '',
                  iconClassName,
                )}
                size={iconSize}
              />
            )}
          </div>
        }
      </button>
    </WrapperComp>
  );
}
